import {
	Button,
	Colors,
	DialogContainer,
	Modal,
	ModalFooter,
	ModalInputBox,
	ModalTitle,
	ModalTitleContainer,
	TextFieldWrapper,
} from "@factorearth/component-library";
import Svg from "components/Svg/Svg";
import React, { useRef, useState } from "react";
import { FiX } from "react-icons/fi";
import styles from "./CsvUploadModal.module.scss";
import { ChangePreview } from "services/gisUpload/types";

interface CsvUploadModalProps {
	accept: string;
	color: Colors;
	onFileUpload: (file: File | null) => Promise<void>;
	onApplyImport: (preview: ChangePreview | null) => Promise<void>;
	isOpen: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	csvPreview: ChangePreview | null;
}

function CsvUploadModal(props: CsvUploadModalProps) {
	const { accept, setOpen, isOpen, color, onFileUpload, onApplyImport, csvPreview } = props;

	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [processing, setProcessing] = useState(false);

	const fileInputRef = useRef<HTMLInputElement>(null);

	async function handleFileUpload(file: File) {
		if (!file) return;

		setProcessing(true);
		try {
			await onFileUpload(file);
		} finally {
			setProcessing(false);
		}
	}

	async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
		const file = e.target.files?.[0] || null;
		setSelectedFile(file);

		if (file) {
			// Auto-trigger upload when file is selected
			await handleFileUpload(file);
		}
	}

	function handleApplyImport() {
		// Close modal immediately and let toast system handle feedback
		setOpen(false);
		setSelectedFile(null);
		onApplyImport(csvPreview);
	}

	async function handleCancel() {
		setOpen(false);
		setSelectedFile(null);
		// Clear the preview in parent component
		await onFileUpload(null);
	}

	const renderUploadSection = () => (
		<>
			<ModalTitleContainer>
				<ModalTitle>Upload GIS CSV</ModalTitle>
				<FiX onClick={handleCancel} size={24} />
			</ModalTitleContainer>
			<ModalInputBox>
				<div className={styles.inputContainer}>
					<label htmlFor="csv-file-upload">
						<Button
							colorPalette={color}
							style={{ whiteSpace: "nowrap", height: "38px" }}
							variant="outlined"
							label="Choose File"
							onClick={() => fileInputRef.current?.click()}
						/>
					</label>
					<TextFieldWrapper colorPalette={color} value={selectedFile?.name || "No File Chosen"} disabled />
					<input
						id="csv-file-upload"
						type="file"
						ref={fileInputRef}
						onChange={handleFileChange}
						onClick={() => setSelectedFile(null)}
						accept={accept}
						name="upload-csv-input"
						className={styles.uploadCsv_input}
					/>
				</div>
			</ModalInputBox>
			{!selectedFile && (
				<ModalFooter>
					<Button variant="outlined" colorPalette={color} label="Cancel" onClick={handleCancel} />
				</ModalFooter>
			)}
		</>
	);

	const renderPreviewSection = () => (
		<>
			<ModalTitleContainer>
				<ModalTitle>CSV Import Confirmation</ModalTitle>
				<FiX onClick={handleCancel} size={24} />
			</ModalTitleContainer>
			<ModalInputBox>
				<div className={styles.inputContainer}>
					<label htmlFor="csv-file-preview">
						<Button
							colorPalette={color}
							style={{ whiteSpace: "nowrap", height: "38px" }}
							variant="outlined"
							label="Change File"
							onClick={() => fileInputRef.current?.click()}
						/>
					</label>
					<TextFieldWrapper colorPalette={color} value={selectedFile?.name || "No File Chosen"} disabled />
					<input
						id="csv-file-preview"
						type="file"
						ref={fileInputRef}
						onChange={handleFileChange}
						onClick={() => setSelectedFile(null)}
						accept={accept}
						name="upload-csv-preview-input"
						className={styles.uploadCsv_input}
					/>
				</div>
				<div className={styles.previewContainer}>
					<div className={styles.previewStats}>
						<p>
							<strong>CSV Rows Processed:</strong> {csvPreview?.stats?.rowsProcessed || 0}
						</p>
						<p>
							<strong>Fields Found:</strong> {csvPreview?.stats?.documentsForBatch.length || 0}
						</p>
						{/* <p>
							<strong>Rows Updated:</strong> {csvPreview?.stats?.rowsUpdated || 0}
						</p>
						<p>
							<strong>Additional Table Rows:</strong> {csvPreview?.stats?.rowsInserted || 0}
						</p> */}
						<p className={styles.homelessRows}>
							<strong>Rows Without Home:</strong>{" "}
							{(csvPreview?.stats?.rowsNoGroupMatch || 0) +
								(csvPreview?.stats?.rowsNoIdentityValue || 0) +
								(csvPreview?.stats?.rowsIdentityNotFound || 0)}
						</p>
						{(csvPreview?.stats?.rowsNoGroupMatch || 0) +
							(csvPreview?.stats?.rowsNoIdentityValue || 0) +
							(csvPreview?.stats?.rowsIdentityNotFound || 0) >
							0 && (
							<div className={styles.homelessBreakdown}>
								<p>
									<strong>Breakdown:</strong>
								</p>
								{(csvPreview?.stats?.rowsNoGroupMatch || 0) > 0 && (
									<p className={styles.breakdownItem}>No group match: {csvPreview?.stats?.rowsNoGroupMatch}</p>
								)}
								{(csvPreview?.stats?.rowsNoIdentityValue || 0) > 0 && (
									<p className={styles.breakdownItem}>No identity value: {csvPreview?.stats?.rowsNoIdentityValue}</p>
								)}
								{(csvPreview?.stats?.rowsIdentityNotFound || 0) > 0 && (
									<p className={styles.breakdownItem}>Identity not found: {csvPreview?.stats?.rowsIdentityNotFound}</p>
								)}
							</div>
						)}
						<p className={styles.confirmationMessage}>
							<strong>Click submit to confirm and import.</strong>
						</p>
						{csvPreview?.warnings && csvPreview.warnings.length > 0 && (
							<div className={styles.warningsSection}>
								<strong className={styles.warningsTitle}>Warnings:</strong>
								<ul className={styles.warningsList}>
									{csvPreview.warnings.map((warning: any, i: number) => (
										<li key={i} className={styles.warningItem}>
											{warning}
										</li>
									))}
								</ul>
							</div>
						)}
					</div>
				</div>
			</ModalInputBox>
			<ModalFooter>
				<div className={styles.previewActions}>
					<Button variant="filled" colorPalette={color} label="Submit" onClick={handleApplyImport} />
					<Button variant="outlined" colorPalette={color} label="Cancel" onClick={handleCancel} />
				</div>
			</ModalFooter>
		</>
	);

	const renderProcessingSection = () => (
		<>
			<ModalTitleContainer>
				<ModalTitle>Processing CSV</ModalTitle>
			</ModalTitleContainer>
			<ModalInputBox>
				<div className={styles.processingContainer}>
					<Svg icon="SPINNER" />
					<p>Processing CSV file...</p>
				</div>
			</ModalInputBox>
		</>
	);

	return (
		<Modal colorPalette={color} open={isOpen} setOpen={setOpen}>
			<DialogContainer onClick={(e) => e.stopPropagation()}>
				{processing ? renderProcessingSection() : csvPreview ? renderPreviewSection() : renderUploadSection()}
			</DialogContainer>
		</Modal>
	);
}

export default CsvUploadModal;
